import { ComponentPropsWithoutRef, FC, ReactNode } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Stack,
  SxProps,
  Typography,
} from '@mui/material';

type WaveUIAccordionProps = Omit<
  ComponentPropsWithoutRef<typeof Accordion>,
  'title'
> & {
  title: ReactNode;
  children: ReactNode;
  actions?: [
    {
      label: string;
      icon: ReactNode;
      sx?: SxProps;
      disabled?: boolean;
      onClick: () => void;
    },
  ];
  summaryProps?: ComponentPropsWithoutRef<typeof AccordionSummary>;
};

const WaveUIAccordion: FC<WaveUIAccordionProps> = ({
  title,
  children,
  defaultExpanded = false,
  disableGutters = true,
  square = true,
  elevation = 3,
  sx,
  actions,
  summaryProps,
  ...props
}) => {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters={disableGutters}
      square={square}
      elevation={elevation}
      sx={{
        'borderRadius': 4,
        'borderBottom': 0,
        '&:before': {
          display: 'none',
        },
        ...sx,
      }}
      {...props}
    >
      {!summaryProps?.hidden && (
        <AccordionSummary
          expandIcon={<KeyboardArrowDownIcon />}
          {...summaryProps}
        >
          {typeof title === 'string' ? (
            <Typography variant='body1'>{title}</Typography>
          ) : (
            title
          )}

          {actions && (
            <Stack
              direction='row'
              spacing={1}
              ml='auto'
              mr={1}
              alignItems='center'
            >
              {actions.map((action) => (
                <IconButton
                  key={action.label}
                  sx={action.sx}
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    return action.onClick();
                  }}
                >
                  {action.icon}
                </IconButton>
              ))}
            </Stack>
          )}
        </AccordionSummary>
      )}

      <AccordionDetails
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

export default WaveUIAccordion;
